import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Pencil, GripVertical, Film, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import axios from "axios";
import { API } from "@/lib/api";
import { useStudioApi, errMsg } from "@/lib/api";

const MAX_BYTES = 2 * 1024 * 1024 * 1024;
const CHUNK = 8 * 1024 * 1024;
const fmtSize = (b) => (b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

const VideoRow = ({ v, i, onRename, onDelete, drag }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(v.title);
  return (
    <li draggable={!editing} {...drag} className="queue-row flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2.5" data-testid={`studio-video-item-${i}`}>
      <GripVertical className="w-4 h-4 text-white/30 shrink-0 cursor-grab" />
      <Film className="w-4 h-4 text-[color:var(--gold)] shrink-0" />
      {editing ? (
        <>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-8 bg-black/30" data-testid={`studio-video-title-input-${i}`} />
          <button onClick={() => { onRename(v, title); setEditing(false); }} className="p-1.5 rounded hover:bg-white/10 text-[color:var(--green)]" data-testid={`studio-video-save-${i}`}><Check className="w-4 h-4" /></button>
          <button onClick={() => { setTitle(v.title); setEditing(false); }} className="p-1.5 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
        </>
      ) : (
        <>
          <p className="flex-1 min-w-0 truncate text-sm font-semibold">{v.title}</p>
          <span className="font-mono text-xs text-[color:var(--lr-muted)] hidden sm:block">{fmtSize(v.size)}</span>
          <button onClick={() => setEditing(true)} className="p-1.5 rounded hover:bg-white/10" data-testid={`studio-video-edit-${i}`}><Pencil className="w-4 h-4" /></button>
          <button onClick={() => onDelete(v)} className="p-1.5 rounded hover:bg-white/10 text-[color:var(--red)]" data-testid={`studio-video-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
        </>
      )}
    </li>
  );
};

export const VideosPanel = () => {
  const call = useStudioApi();
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [loop, setLoop] = useState(null);
  const [drag, setDrag] = useState(null);
  const input = useRef(null);

  const load = useCallback(() => call("get", "/videos").then(setVideos).catch((e) => toast.error(errMsg(e))), [call]);
  useEffect(() => { load(); axios.get(`${API}/station/status`).then(({ data }) => setLoop((cur) => (cur === null ? data.station.video_loop ?? true : cur))); }, [load]);

  const saveLoop = async (v) => {
    setLoop(v);
    try { await call("put", "/studio/video-settings", { loop: v }); toast.success(v ? "Videos will loop" : "Videos play once, then the next one"); }
    catch (e) { setLoop(!v); toast.error(errMsg(e)); }
  };

  const sendFile = async (f, idx, total) => {
    const { upload_id } = await call("post", "/studio/videos/uploads", { filename: f.name, size: f.size });
    let offset = 0, fails = 0;
    while (offset < f.size) {
      const piece = f.slice(offset, Math.min(offset + CHUNK, f.size));
      try {
        offset = (await call("put", `/studio/videos/uploads/${upload_id}?offset=${offset}`, piece, { headers: { "Content-Type": "application/octet-stream" } })).received;
        fails = 0;
      } catch (e) {
        if (++fails >= 4) throw e;
        await new Promise((r) => setTimeout(r, 1500));
        offset = (await call("get", `/studio/videos/uploads/${upload_id}`)).received; // resume where the server stopped
      }
      setProgress({ name: f.name, pct: Math.round((offset / f.size) * 100), idx, total });
    }
    setProgress({ name: f.name, pct: 100, idx, total, saving: true });
    await call("post", `/studio/videos/uploads/${upload_id}/complete`);
    for (;;) {
      await new Promise((r) => setTimeout(r, 2000));
      const st = await call("get", `/studio/videos/uploads/${upload_id}`);
      if (st.status === "done") return st;
      if (st.status === "error") throw new Error("Saving to storage failed, try again");
    }
  };

  const upload = async (files) => {
    const ok = [...(files || [])].filter((f) => {
      if (f.size <= MAX_BYTES) return true;
      toast.error(`${f.name}: larger than 2 GB. Compress it (e.g. 1080p H.264) and try again.`);
      return false;
    });
    if (!ok.length) { input.current.value = ""; return; }
    setUploading(true);
    let done = 0;
    for (const [i, f] of ok.entries()) {
      try { await sendFile(f, i + 1, ok.length); done += 1; }
      catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    if (done) toast.success(`${done} video(s) added to the player page`);
    setProgress(null);
    load();
    setUploading(false);
    input.current.value = "";
  };

  const rename = async (v, title) => {
    try { await call("patch", `/studio/videos/${v.id}`, { title }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (v) => {
    if (!window.confirm(`Remove "${v.title}" from the player page?`)) return;
    try { await call("delete", `/studio/videos/${v.id}`); toast.success("Video removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const drop = async (target) => {
    if (drag === null || drag === target) return;
    const ids = videos.map((v) => v.id);
    const [moved] = ids.splice(drag, 1);
    ids.splice(target, 0, moved);
    try { setVideos(await call("put", "/studio/videos/order", { ids })); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="panel p-5 mt-3 flex flex-col gap-4" data-testid="studio-videos-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <span className="eyebrow">Player page videos</span>
          <p className="text-sm text-[color:var(--lr-muted)] mt-1">MP4 videos (up to 2 GB each) show in a video player on the public page. H.264 MP4 plays everywhere. The first one plays by default. Drag to reorder.</p>
        </div>
        <input ref={input} type="file" multiple accept=".mp4,.m4v,.mov,.webm,video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => upload(e.target.files)} data-testid="studio-video-upload-input" />
        <button onClick={() => input.current.click()} disabled={uploading} className="rounded-lg bg-[color:var(--red)] px-5 py-2.5 font-bold flex items-center justify-center gap-2 disabled:opacity-60 shrink-0" data-testid="studio-video-upload-button">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? "Uploading..." : "Upload videos"}
        </button>
      </div>
      {progress && (
        <div className="rounded-lg border border-white/10 bg-black/30 p-3 flex flex-col gap-2" data-testid="studio-video-upload-progress">
          <div className="flex justify-between text-xs font-mono"><span className="truncate">{progress.idx}/{progress.total} · {progress.name}</span><span>{progress.saving ? "saving..." : `${progress.pct}%`}</span></div>
          <Progress value={progress.pct} />
        </div>
      )}
      <label className="flex items-center justify-between rounded-lg border border-white/10 bg-black/30 px-4 py-3 cursor-pointer">
        <span className="text-sm"><b>Loop videos</b><span className="block text-xs text-[color:var(--lr-muted)]">On: the video repeats forever. Off: plays once, then the next video. Viewers can still switch it.</span></span>
        <Switch checked={!!loop} disabled={loop === null} onCheckedChange={saveLoop} data-testid="studio-video-loop-switch" />
      </label>
      {!videos.length && <p className="py-8 text-center text-sm text-[color:var(--lr-muted)]" data-testid="studio-videos-empty">No videos yet. The video section stays hidden on the public page until you add one.</p>}
      <ol className="flex flex-col gap-2" data-testid="studio-videos-list">
        {videos.map((v, i) => (
          <VideoRow key={v.id} v={v} i={i} onRename={rename} onDelete={remove}
            drag={{ onDragStart: () => setDrag(i), onDragEnd: () => setDrag(null), onDragOver: (e) => e.preventDefault(), onDrop: () => drop(i) }} />
        ))}
      </ol>
    </section>
  );
};
